import { type Table } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@avanta/shared/components/Text';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@avanta/shared/components/ui/DropdownMenu';
import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@avanta/shared/components/ui/Pagination';
import { Skeleton } from '@avanta/shared/components/ui/Skeleton';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { getPageSizeOptions } from './getPageSizeOptions';
import { useTablePagination } from './useTablePagination';

interface PaginationProps<T> {
  table: Table<T>;
  /** Shows an Auto option that keeps page size fitted to the viewport. */
  autoPageSize?: boolean;
  isAutoPageSize?: boolean;
  onPageSizeChange?: (isAuto: boolean) => void;
  /** Replaces the "showing X of Y" summary with a skeleton while rows and total are updating. */
  isLoading?: boolean;
  /** Replaces the whole pagination with a skeleton while the page count is being recalculated. */
  isPageCountLoading?: boolean;
}

export const Pagination = <T,>({
  table,
  autoPageSize = false,
  isAutoPageSize = false,
  onPageSizeChange,
  isLoading = false,
  isPageCountLoading = false,
}: PaginationProps<T>): ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const {
    canNextPage,
    canPreviousPage,
    currentPage,
    pageNumbers,
    pageSize,
    totalPages,
    nextPage,
    previousPage,
    setPageIndex,
  } = useTablePagination({ table });
  const shown = table.getRowModel().rows.length;
  const total = table.getRowCount();
  const hasMultiplePages = totalPages > 1;
  const displayedPageSize = Math.min(pageSize, total);
  const pageSizeOptions = getPageSizeOptions(total);
  const autoLabel = t('Pagination.auto');
  const triggerLabel = autoPageSize && isAutoPageSize ? autoLabel : String(displayedPageSize);

  if (total === 0 || !hasMultiplePages) return null;

  const applyPageSize = (size: number, isAuto: boolean): void => {
    table.setPagination(state => ({
      ...state,
      pageIndex: 0,
      pageSize: size,
    }));
    onPageSizeChange?.(isAuto);
  };

  if (isPageCountLoading) {
    return (
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-64 am-lg:col-start-2 max-am-lg:w-40" />
        <Skeleton className="col-start-3 h-8 w-32 justify-self-end" />
      </div>
    );
  }

  return (
    <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      {isLoading ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <Text tag="p" variant="text-xs-regular" className="text-gray-400">
          {t('Pagination.showing', { shown, total })}
        </Text>
      )}
      <PaginationComponent className="am-lg:col-start-2">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious onClick={previousPage} disabled={!canPreviousPage} />
          </PaginationItem>
          {pageNumbers.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }
            return (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => setPageIndex(page - 1)}
                  isActive={currentPage === page}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            );
          })}
          <PaginationItem>
            <PaginationNext onClick={nextPage} disabled={!canNextPage} />
          </PaginationItem>
        </PaginationContent>
      </PaginationComponent>
      <div className="col-start-3 flex items-center justify-end gap-1">
        <Text tag="span" variant="text-xs-regular" className="text-gray-400">
          {t('Pagination.show')}
        </Text>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('Pagination.show')}
            className="box-border flex h-8 min-w-18.75 shrink-0 items-center justify-center gap-2 rounded-none border border-plum-200 bg-white px-4 py-2.5 outline-none focus:outline-none focus-visible:outline-none"
          >
            <Text tag="span" variant="text-xs-regular" className="text-denim-500">
              {triggerLabel}
            </Text>
            <ChevronDown className="size-5 shrink-0 text-denim-500" strokeWidth={1.25} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {autoPageSize ? (
              <DropdownMenuItem
                onSelect={() => applyPageSize(Math.max(displayedPageSize, 1), true)}
              >
                {autoLabel}
              </DropdownMenuItem>
            ) : null}
            {pageSizeOptions.map(size => (
              <DropdownMenuItem key={size} onSelect={() => applyPageSize(size, false)}>
                {String(size)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
